import { useEffect, useMemo, useState, type ReactNode } from 'react'

import { ChevronLeft, ChevronRight } from 'lucide-react'

import { CoverageOfficersHeaderRow } from './CoverageOfficersHeaderRow'
import { CoverageOfficerTableRow } from './CoverageOfficerTableRow'
import { CoverageSitesHeaderRow } from './CoverageSitesHeaderRow'
import { CoverageSiteTableRow } from './CoverageSiteTableRow'
import { PAGE_SIZE, type ReportFilters } from '../../../api/reportsApi'
import { useSurveysReport } from '../../../hooks/useSurveysReport'
import { paginate, type Page } from '../../../lib/paginate'
import { reportCaption } from '../../../lib/reports-paging'
import { useGisCoverageView } from '../../../stores/gisCoverageViewStore'
import { GroupedSections } from '../../ui/GroupedSections'
import { SegmentedControl } from '../../ui/SegmentedControl'
import { ReportTable } from '../reports/ReportTable'
import { ShowMoreButton } from '../reports/ShowMoreButton'
import { SurveyStatusTable } from '../reports/SurveyStatusTable'

import type { GisSite } from '../../../api/types'
import type { CoverageGroupBy, CoverageListMode, SiteGroup } from '../../../lib/coverage-board'
import type { MandalOfficerRow } from '../../../lib/mandal-officers'

const PER_PAGE_OPTIONS = [10, 25, 50] as const

/** Centered muted note for the empty branches. */
const EmptyNote = ({ label }: { label: string }) => (
  <div className="text-gis-soft p-6 text-center text-[12px]">{label}</div>
)

interface ListFooterProps {
  p: Page<unknown>
  noun: string
  perPage: number
  onPage: (page: number) => void
  onPerPage: (perPage: number) => void
}

/** The shared pagination footer ("Showing X to Y of Z" + prev/next + rows-per-page). */
function ListFooter({ p, noun, perPage, onPage, onPerPage }: ListFooterProps) {
  if (p.total === 0) {
    return null
  }
  return (
    <footer className="flex items-center gap-3 border-t border-gis-line-2 px-3 py-[8px] text-[11.5px] text-gis-soft">
      <span>
        Showing {p.from} to {p.to} of {p.total} {noun}
      </span>
      <div className="ml-auto flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPage(p.page - 1)}
          disabled={p.page <= 1}
          aria-label="Previous page"
          className="grid h-[26px] w-[26px] place-items-center rounded-md text-gis-soft transition-colors hover:bg-gis-track active:brightness-95 disabled:opacity-40 focus-visible:ring-2 focus-visible:ring-blue-500/60"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="px-1 text-[11.5px] font-semibold tabular-nums text-gis-ink">{p.page}</span>
        <span className="text-gis-muted">/ {p.pageCount}</span>
        <button
          type="button"
          onClick={() => onPage(p.page + 1)}
          disabled={p.page >= p.pageCount}
          aria-label="Next page"
          className="grid h-[26px] w-[26px] place-items-center rounded-md text-gis-soft transition-colors hover:bg-gis-track active:brightness-95 disabled:opacity-40 focus-visible:ring-2 focus-visible:ring-blue-500/60"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      {/* One-click page-size switch — same control as the Issues footer. */}
      <span className="flex items-center gap-1.5">
        Rows
        <SegmentedControl<'10' | '25' | '50'>
          aria-label="Rows per page"
          size="xs"
          track="b"
          shape="rounded"
          items={PER_PAGE_OPTIONS.map((n) => ({ key: String(n) as '10' | '25' | '50', label: String(n) }))}
          value={String(perPage) as '10' | '25' | '50'}
          onChange={(k) => onPerPage(Number(k))}
        />
      </span>
    </footer>
  )
}

/** The scroll region every presentation shares. */
const ScrollRegion = ({ children }: { children: ReactNode }) => (
  <div className="@container/list min-h-0 flex-1 overflow-y-auto p-2">{children}</div>
)

/** The Surveys view: the gated pending/missed report table (status + export live in the toolbar). */
function SurveysReportView({ filters }: { filters: ReportFilters }) {
  const status = useGisCoverageView((s) => s.surveyStatus)
  const { data, isPending, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useSurveysReport(filters, status)
  const rows = useMemo(() => data?.pages.flatMap((p) => p.rows) ?? [], [data?.pages])
  const totalCount = data?.pages[0]?.totalCount ?? null
  return (
    <ScrollRegion>
      <div className="p-1">
        <ReportTable
          bare
          title="Survey status"
          isPending={isPending}
          isError={isError}
          isEmpty={rows.length === 0}
          emptyLabel={`No ${status} surveys in this period.`}
          onRetry={() => void refetch()}
          caption={reportCaption(rows.length, totalCount, PAGE_SIZE)}
          footer={
            hasNextPage ? (
              <ShowMoreButton onClick={() => void fetchNextPage()} loading={isFetchingNextPage} />
            ) : undefined
          }
        >
          <SurveyStatusTable rows={rows} status={status} />
        </ReportTable>
      </div>
    </ScrollRegion>
  )
}

interface CoverageListBodyProps {
  mode: CoverageListMode
  /** Pre-filtered (toolbar facets + search + mandal) + worst-first sorted. */
  sites: GisSite[]
  /** Pre-computed sections for `groupBy` (empty when 'none'); Sites view only. */
  groups: SiteGroup[]
  groupBy: CoverageGroupBy
  /** Officer rollup over the SAME filtered site set (worst completion first). */
  officers: MandalOfficerRow[]
  /** Any toolbar filter active — words the sites empty state truthfully. */
  filtered: boolean
  /** `{ scope, mandal }` for the Surveys report view. */
  reportFilters: ReportFilters
  onSelectSite: (id: string) => void
  onHover: (id: string | null) => void
}

/**
 * The Coverage card body (V5): one of three views — grouped/paginated Sites table, the Officers
 * rollup table, or the pending/missed Surveys report. Mirrors `IssuesListBody`: grouping switches
 * pagination off (the region scrolls); the flat views paginate with the shared footer.
 */
export function CoverageListBody(p: CoverageListBodyProps) {
  const [perPage, setPerPage] = useState<number>(25)
  const [page, setPage] = useState(1)
  const { mode, sites, groups, groupBy, officers, filtered } = p

  // Reset to page 1 whenever the ACTIVE view's rows, the view, or the page size change.
  const activeRows = mode === 'sites' ? sites : officers
  useEffect(() => {
    setPage(1)
  }, [activeRows, perPage, mode])

  if (mode === 'surveys') {
    return <SurveysReportView filters={p.reportFilters} />
  }

  const siteRows = (rows: GisSite[]): ReactNode => (
    <div className="space-y-1">
      {rows.map((s) => (
        <CoverageSiteTableRow key={s.id} site={s} onSelect={p.onSelectSite} onHover={p.onHover} />
      ))}
    </div>
  )

  if (mode === 'sites') {
    if (sites.length === 0) {
      return <EmptyNote label={filtered ? 'No sites match these filters.' : 'No sites in this selection.'} />
    }
    if (groupBy !== 'none') {
      return (
        <ScrollRegion>
          <GroupedSections groups={groups} renderGroup={siteRows} />
        </ScrollRegion>
      )
    }
    const pg = paginate(sites, page, perPage)
    return (
      <>
        <ScrollRegion>
          <CoverageSitesHeaderRow />
          <div className="pt-1">{siteRows(pg.items)}</div>
        </ScrollRegion>
        <ListFooter p={pg} noun="sites" perPage={perPage} onPage={setPage} onPerPage={setPerPage} />
      </>
    )
  }

  if (officers.length === 0) {
    return <EmptyNote label={filtered ? 'No officers match these filters.' : 'No officers to show.'} />
  }
  const pg = paginate(officers, page, perPage)
  return (
    <>
      <ScrollRegion>
        <CoverageOfficersHeaderRow />
        <div className="space-y-1 pt-1">
          {pg.items.map((r) => (
            <CoverageOfficerTableRow key={r.officerId ?? '_unassigned'} row={r} />
          ))}
        </div>
      </ScrollRegion>
      <ListFooter p={pg} noun="officers" perPage={perPage} onPage={setPage} onPerPage={setPerPage} />
    </>
  )
}
